import { WorkOrderFormData } from '@/components/Movements/WorkOrders/WorkOrderForm';
import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  Group,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import EmployeesInfiniteSelectField from '@/components/_common/EmployeesInfiniteSelectField';
import InsurerInfiniteSelectField from '@/components/_common/InsurerInfiniteSelectField';
import PoliciesCardList from '@/components/_common/PoliciesCardList';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import WorkOrderComplementTypeSelect from '@/components/_common/WorkOrderComplementTypeSelect';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { CustomerVehicleReduced } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import { usePortoPoliciesList } from '@/core/domain/porto/policies/porto-policies.hooks';
import {
  useCreateWorkOrder,
  useWorkOrderCreateByPorto,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import {
  CreateWorkOrderRequest,
  CustomerType,
  WorkOrderCreateByPolicyCompanyCode,
  WorkOrderCreateByPorto,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import {
  WorkOrderComplementTankLevel,
  WorkOrderComplementType,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useWorkorderConfigurationCompanyQuery } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import { useDefaultEmptyFieldError } from '@/hooks';
import { isAppPortocar } from '@/index';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { DateTimeFormat } from '@/utils/moment';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  notification,
} from 'antd';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { FaCar } from 'react-icons/fa';
import { useIntl } from 'react-intl';

interface Props {
  opened: boolean;
  onClose: VoidFunction;
  vehicle?: CustomerVehicleReduced;
}

export default function VisionWorkorderCreate({ vehicle, ...props }: Props) {
  const [form] = Form.useForm<WorkOrderFormData>();

  const hasFuelEletric = useMemo(() => {
    return vehicle?.fuel?.name?.toLowerCase()?.includes('elétrico');
  }, [vehicle]);

  const companyIdField = Form.useWatch('companyId', form);
  const policyField = Form.useWatch('policy', form);

  const workorderCreate = useCreateWorkOrder();
  const workOrderCreateByPolicy = useWorkOrderCreateByPorto();

  const queryChanel = useQueryChanel();

  const policiesList = usePortoPoliciesList(
    vehicle?.licensePlate,
    props.opened && isAppPortocar,
  );

  useEffect(() => {
    if (policiesList.data?.length) {
      const policy = policiesList.data[0];

      const { email, phone, ...restCustomer } = policy.customer;

      form.setFieldValue('policy', {
        companyCode: policy.companyCode,
        policyNumber: policy.policyNumber,
        customer: {
          ...restCustomer,
          contacts: { email, phone },
        },
        vehicle: policy.vehicle,
        benefits: policy.benefits,
      });
    }
  }, [policiesList.dataUpdatedAt]);

  const workoOrderConfig =
    useWorkorderConfigurationCompanyQuery(companyIdField);
  const config = workoOrderConfig.data;

  const intl = useIntl();

  const fieldRequired = useDefaultEmptyFieldError();

  const handleClose = () => {
    form.resetFields();
    props.onClose();
  };

  const handleSubmit = async () => {
    try {
      const { policy, ...values } = await form.validateFields();

      if (policy?.policyNumber) {
        const obj: WorkOrderCreateByPorto = {
          ...values,
          vehicleId: vehicle?.id,
          customerId: vehicle?.customerId,
          companyCode:
            `domain.enums.company_code.${policy?.companyCode?.toLowerCase()}` as WorkOrderCreateByPolicyCompanyCode,
          entryDate: values.entryDate
            ? values.entryDate.format(DateTimeFormat)
            : undefined,
          estimatedLeaveDate: values.estimatedLeaveDate
            ? values.estimatedLeaveDate.format(DateTimeFormat)
            : undefined,
          lastOilChange: values.lastOilChange
            ? values.lastOilChange.format(DateTimeFormat)
            : undefined,
          policyNumber: policy?.policyNumber as string,
          customer: policy?.customer,
          vehicle: {
            ...policy?.vehicle,
            licensePlate: vehicle?.licensePlate,
            prisma: values.prisma,
            currentKilometers: values.currentKilometers,
            tankLevel: values.tankLevel,
          },
          applyPartnerDiscount: true,
        };

        await workOrderCreateByPolicy.mutateAsync(obj);
      } else {
        const obj: CreateWorkOrderRequest = {
          ...values,
          vehicleId: vehicle?.id,
          entryDate: values.entryDate
            ? values.entryDate.format(DateTimeFormat)
            : undefined,
          estimatedLeaveDate: values.estimatedLeaveDate
            ? values.estimatedLeaveDate.format(DateTimeFormat)
            : undefined,
          lastOilChange: values.lastOilChange
            ? values.lastOilChange.format(DateTimeFormat)
            : undefined,
        };

        await workorderCreate.mutateAsync(obj);

        notification.success({
          message: intl.formatMessage({
            id: 'workOrders.create.notifications.success.title',
          }),
          description: intl.formatMessage({
            id: 'workOrders.create.notifications.success.description',
          }),
        });
      }

      queryChanel({ refetchQueries: [['workOrders/list']] });

      handleClose();
    } catch {}
  };

  const [selectedCustomerType, setSelectedCustomerType] =
    useState<CustomerType>();

  const handleSelectCustomerType = (type?: CustomerType) => {
    form.resetFields([
      'insurerId',
      'accidentNumber',
      'insurerDeductibleAmount',
    ]);

    setSelectedCustomerType(type);
  };

  const disableFields = workorderCreate.isLoading;

  return (
    <Modal
      open={props.opened}
      onCancel={handleClose}
      destroyOnClose
      title="Cadastro rápido de orçamento"
      confirmLoading={workorderCreate.isLoading}
      style={{ top: 24 }}
      footer={
        <SpaceForButton justifyContent="flex-end">
          <Button
            block
            onClick={handleClose}
            disabled={workorderCreate.isLoading}
          >
            Cancelar
          </Button>

          <Button
            block
            type="primary"
            onClick={handleSubmit}
            disabled={policiesList.isFetching}
            loading={
              workorderCreate.isLoading || workOrderCreateByPolicy.isLoading
            }
          >
            Criar orçamento
          </Button>
        </SpaceForButton>
      }
    >
      <Form form={form} preserve={false}>
        <Form.Item
          hidden
          name={['policy', 'companyCode']}
          rules={
            !!policiesList.data?.length
              ? [{ required: true, message: 'Selecione uma apólice' }]
              : undefined
          }
        />
        <Form.Item hidden name={['policy', 'policyNumber']} />
        <Form.Item hidden name={['policy', 'customer']} />
        <Form.Item hidden name={['policy', 'vehicle']} />
        <Form.Item hidden name={'licensePlate'} />

        <Row gutter={[24, 16]}>
          <Col span={24}>
            <Group gap={16} style={{ marginBottom: 16 }}>
              <FaCar size={24} />

              <div>
                <div style={{ fontSize: 14, color: 'darkgray', lineHeight: 1 }}>
                  Veículo
                </div>
                <strong>{`${vehicle?.licensePlate} • ${vehicle?.modelName}`}</strong>
              </div>
            </Group>
          </Col>

          {isAppPortocar && (
            <Col span={24}>
              <strong
                style={{
                  display: 'inline-block',
                  fontSize: '1.1rem',
                }}
              >
                Selecione uma apolíce
              </strong>

              <Form.Item shouldUpdate noStyle>
                {({ getFieldError }) => {
                  const errors = getFieldError(['policy', 'companyCode']);
                  return errors.length ? (
                    <span style={{ color: 'red' }}>{errors[0]}</span>
                  ) : null;
                }}
              </Form.Item>

              <PoliciesCardList
                loading={policiesList.isLoading || policiesList.isFetching}
                licensePlate={vehicle?.licensePlate}
                policies={policiesList.data}
                policyNumber={policyField?.policyNumber}
                onSelect={(policy) => {
                  const { email, phone, ...restCustomer } = policy.customer;

                  form.setFieldsValue({
                    licensePlate: vehicle?.licensePlate,
                    policy: {
                      companyCode: policy.companyCode,
                      policyNumber: policy.policyNumber,
                      customer: {
                        ...restCustomer,
                        contacts: { email, phone },
                      },
                      vehicle: policy.vehicle,
                      benefits: policy.benefits,
                    },
                  });
                }}
              />
            </Col>
          )}

          <Col span={24}>
            <FloatingFormItem
              name="companyId"
              rules={[fieldRequired]}
              label={intl.formatMessage({
                id: 'workOrders.domain.company',
              })}
              getValueFromEvent={(id) => {
                form.setFieldValue('estimatorId', undefined);
                return id;
              }}
            >
              <CompaniesInfiniteSelectField
                queryEnable
                disabled={disableFields}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="estimatorId"
              rules={[fieldRequired]}
              label={intl.formatMessage({
                id: 'workOrders.domain.complements.estimator',
              })}
            >
              <EmployeesInfiniteSelectField
                queryFilter={{
                  isActive: true,
                  companyId: companyIdField,
                  estimator: true,
                }}
                queryEnable={!!companyIdField}
                disabled={!companyIdField || disableFields}
                workOrderNotSelectOtherEstimator={
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_SELECT_ANOTHER_EMPLOYEE,
                  ])
                }
                key={companyIdField}
              />
            </FloatingFormItem>
          </Col>

          <Col span={12}>
            <FloatingFormItem
              name="workOrderType"
              rules={[fieldRequired]}
              label={intl.formatMessage({
                id: 'workOrders.domain.complements.type',
              })}
              initialValue={WorkOrderComplementType.WorkOrder}
            >
              <WorkOrderComplementTypeSelect disabled={disableFields} />
            </FloatingFormItem>
          </Col>

          <Col span={12}>
            <FloatingFormItem
              name="customerType"
              rules={[fieldRequired]}
              label={intl.formatMessage({
                id: 'workOrders.domain.complements.customerType',
              })}
              initialValue={CustomerType.Private}
            >
              <Select
                allowClear
                style={{ width: '100%' }}
                onChange={handleSelectCustomerType}
                options={Object.values(CustomerType).map((a) => ({
                  label: intl.formatMessage({ id: `workOrders.${a}` }),
                  value: a,
                }))}
                disabled={disableFields}
              />
            </FloatingFormItem>
          </Col>

          {selectedCustomerType === CustomerType.Insurance ? (
            <>
              <Col span={24}>
                <FloatingFormItem
                  name="insurerId"
                  label={intl.formatMessage({
                    id: 'workOrders.domain.complements.insurer',
                  })}
                  rules={
                    hasPermissions([Authorities.REGISTER_INSURER_VIEW])
                      ? [fieldRequired]
                      : []
                  }
                >
                  <InsurerInfiniteSelectField
                    disabled={
                      !hasPermissions([Authorities.REGISTER_INSURER_VIEW]) ||
                      disableFields
                    }
                  />
                </FloatingFormItem>
              </Col>

              <Col span={12}>
                <FloatingFormItem
                  name="accidentNumber"
                  label={intl.formatMessage({
                    id: 'workOrders.domain.complements.accidentNumber',
                  })}
                >
                  <Input disabled={disableFields} />
                </FloatingFormItem>
              </Col>

              <Col span={12}>
                <FloatingFormItem
                  name="insurerDeductibleAmount"
                  initialValue={0}
                  label={intl.formatMessage({
                    id: 'workOrders.domain.complements.insurerDeductibleAmount',
                  })}
                >
                  <InputDecimal disabled={disableFields} />
                </FloatingFormItem>
              </Col>
            </>
          ) : null}

          <Col span={24} md={12}>
            <FloatingFormItem
              name="entryDate"
              rules={[fieldRequired]}
              label={intl.formatMessage({
                id: 'workOrders.domain.complements.entryDate',
              })}
              initialValue={moment()}
            >
              <InputDate showTime disabled={disableFields} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12}>
            <FloatingFormItem
              name="estimatedLeaveDate"
              label={intl.formatMessage({
                id: 'workOrders.domain.complements.estimatedLeaveDate',
              })}
              rules={[
                {
                  required: !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_ALLOW_EMPTY_ESTIMATED_LEAVE_DATE,
                  ]),
                },
              ]}
            >
              <InputDate showTime disabled={disableFields} />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <Row gutter={[16, 16]}>
              <Col sm={8}>
                <FloatingFormItem
                  name="prisma"
                  label="Prisma"
                  rules={[
                    {
                      required: config?.prisma,
                      message: 'Campo obrigatório',
                    },
                  ]}
                >
                  <Input />
                </FloatingFormItem>
              </Col>

              <Col sm={8}>
                <FloatingFormItem
                  name="currentKilometers"
                  label="Km atual"
                  rules={[
                    {
                      required: config?.isKmRequired || isAppPortocar,
                      message: 'Campo obrigatório',
                    },
                  ]}
                >
                  <InputDecimal fractions={0} min={0} max={5000000} />
                </FloatingFormItem>
              </Col>

              <Col sm={8}>
                <FloatingFormItem
                  name="tanklevel"
                  label="Nível do tanque"
                  rules={[
                    {
                      required: config?.tankLevel,
                      message: 'Campo obrigatório',
                    },
                  ]}
                >
                  <Select
                    allowClear
                    style={{ width: '100%' }}
                    options={Object.values(WorkOrderComplementTankLevel).map(
                      (a) => ({
                        label: intl.formatMessage({
                          id: `workOrders.${a}`,
                        }),
                        value: a,
                      }),
                    )}
                  />
                </FloatingFormItem>
              </Col>
            </Row>
          </Col>

          {isAppPortocar && (
            <>
              <Col span={12}>
                <FloatingFormItem
                  name="lastOilChange"
                  label={intl.formatMessage({
                    id: 'workOrders.domain.complements.lastOilChange',
                  })}
                  rules={hasFuelEletric ? [] : [fieldRequired]}
                >
                  <InputDate
                    disabledDate={(date) => date.isAfter(moment(), 'day')}
                  />
                </FloatingFormItem>
              </Col>

              <Col span={12}>
                <FloatingFormItem
                  name="lastOilChangeKilometers"
                  label={intl.formatMessage({
                    id: 'workOrders.domain.complements.lastOilChangeKilometers',
                  })}
                  rules={hasFuelEletric ? [] : [fieldRequired]}
                >
                  <InputDecimal fractions={0} min={0} />
                </FloatingFormItem>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </Modal>
  );
}
